import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1536392706976-e486e2ba97af?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85";

const EASE = [0.76, 0, 0.24, 1];

const LineMask = ({ children, delay = 0 }) => (
  <span className="line-mask">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] w-full overflow-hidden bg-[color:var(--bt-bg)]"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={HERO_IMAGE}
          alt="Chandelier"
          className="h-full w-full object-cover"
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--bt-bg)]/85 via-[color:var(--bt-bg)]/45 to-[color:var(--bt-bg)]" />
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_45%,rgba(0,0,0,0)_0%,rgba(10,10,10,0.55)_75%,rgba(10,10,10,0.9)_100%)]" />
      </motion.div>

      {/* Vertical frame lines */}
      <div className="pointer-events-none absolute inset-y-0 left-6 md:left-10 w-px bg-[color:var(--bt-gold-line)]" />
      <div className="pointer-events-none absolute inset-y-0 right-6 md:right-10 w-px bg-[color:var(--bt-gold-line)]" />

      {/* Top eyebrow */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 h-full flex flex-col justify-between px-6 md:px-16 pt-32 pb-10"
      >
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-center gap-4">
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.2, delay: 0.4, ease: EASE }}
              className="origin-left h-px w-16 bg-[color:var(--bt-gold)]"
            />
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              data-testid="hero-eyebrow"
              className="text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-gold)]"
            >
              Season MMXXVI · By Invitation
            </motion.span>
          </div>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.1 }}
            className="hidden md:block text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)]"
          >
            London · Est. 2026
          </motion.span>
        </div>

        {/* Hero headline */}
        <div className="flex-1 flex flex-col justify-center max-w-[1400px]">
          <h1
            data-testid="hero-headline"
            className="font-serif-display text-[color:var(--bt-ink)] text-[13vw] md:text-[10.5vw] leading-[0.9] tracking-tight"
          >
            <LineMask delay={0.15}>The season,</LineMask>
            <LineMask delay={0.3}>
              <span className="italic font-light text-[color:var(--bt-gold)]">
                quietly&nbsp;
              </span>
              <span>curated.</span>
            </LineMask>
          </h1>

          <div className="mt-10 md:mt-14 grid md:grid-cols-12 gap-6 md:gap-10">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
              data-testid="hero-subhead"
              className="md:col-span-5 md:col-start-2 text-[color:var(--bt-ink-dim)] text-base md:text-lg leading-relaxed max-w-xl"
            >
              The private portal to Britain's charity balls, business awards
              and Highland balls - considered on your behalf, and rarely written
              about.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.35, ease: EASE }}
              className="md:col-span-4 md:col-start-9 flex flex-col md:items-end gap-3"
            >
              <div className="flex items-baseline gap-4 md:justify-end">
                <span className="font-serif-display italic text-3xl text-[color:var(--bt-gold)]">
                  147
                </span>
                <span className="text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)]">
                  Evenings · MMXXVI
                </span>
              </div>
              <div className="flex items-baseline gap-4 md:justify-end">
                <span className="font-serif-display italic text-3xl text-[color:var(--bt-gold)]">
                  38
                </span>
                <span className="text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)]">
                  Counties · UK &amp; IE
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.6 }}
          className="flex items-end justify-between"
        >
          <div className="flex items-center gap-3 text-[color:var(--bt-ink-dim)] text-[11px] tracking-widest-brand uppercase">
            <motion.span
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <ArrowDown size={14} />
            </motion.span>
            <span data-testid="hero-scroll-cue">Scroll - Browse the Season</span>
          </div>
          <div className="hidden md:block text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)]">
            No. 001 / Prologue
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
